import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button, Typography } from '@mui/material'
import {
  getGetOrganisationsQueryKey,
  useCreateOrganisation,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { DialogBase } from '@ui/feedback'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import OrganisationFormFields from '../OrganisationFormFields'
import {
  OrganisationFormValues,
  organisationValidationSchema,
} from '../OrganisationFormFields/schema'

interface Props {
  open: boolean
  onClose: () => void
}

const FORM_ID = 'create-organisation-form'

const CreateOrganisationDialog = ({ open, onClose }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { mutate: createOrganisation, isLoading } = useCreateOrganisation<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Organisation erstellen',
        description: 'Legt eine Organisation ohne Mitglieder an',
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetOrganisationsQueryKey())
        enqueueSnackbar('Organisation erfolgreich erstellt', { variant: 'success' })
        onClose()
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen der Organisation'), {
          variant: 'error',
        })
      },
    },
  })

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title="Organisation erstellen"
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    >
      <Form2<OrganisationFormValues>
        id={FORM_ID}
        defaultValues={{ name: '', street: '', zipCode: '', city: '' }}
        validationSchema={organisationValidationSchema}
        onSubmit={values =>
          createOrganisation({
            data: {
              name: values.name.trim(),
              street: values.street.trim(),
              zip_code: values.zipCode.trim(),
              city: values.city.trim(),
            },
          })
        }
      >
        <OrganisationFormFields />
        <Typography variant="caption" color="text.secondary" mt={2} display="block">
          Die Organisation wird ohne Mitglieder angelegt. Nutzer werden anschließend über
          &quot;Organisationen&quot; in der Nutzerliste zugewiesen.
        </Typography>
      </Form2>
      <>
        <Button size="small" onClick={onClose}>
          Abbrechen
        </Button>
        <SaveButton
          size="small"
          loading={isLoading}
          form={FORM_ID}
          type="submit"
          data-cy="btn-save-organisation"
        >
          Erstellen
        </SaveButton>
      </>
    </DialogBase>
  )
}

export default CreateOrganisationDialog
